import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import { faCode } from "@fortawesome/free-solid-svg-icons";
import {
    faPython,
    faLinux,
    faDocker,
} from "@fortawesome/free-brands-svg-icons";
import {
    faShieldHalved,
    faCloud,
    faBug,
    faSkullCrossbones,
    faMagnifyingGlass,
    faCheck,
    faLock,
} from "@fortawesome/free-solid-svg-icons";
import Chip from "@mui/material/Chip";
import "../assets/styles/Expertise.scss";
import isc2CcImage from "../assets/images/ISC2-CC.png";
import awsCcpImage from "../assets/images/image771495085737125892.png";
import sc900Image from "../assets/images/SC-900.png";

/** =============================
 *  Personalized for Leandro Costa
 *  Sections: Cloud & DevSecOps | SOC & Detection | Offensive & Malware
 *  ============================= */

const cloudDevSecOps = [
    "OIDC Authentication • IAM",
    "IaC",
    "DNS, WAF & HTTPS/TLS"
];

const socDetection = [
    "SIEM • SOAR",
    "Threat Hunting",
    "NIST • ISO 27001",
];

const offensiveMalware = [
    "CTFs",
    "Cloud Pentesting",
    "Forensics • Misc",
];

type Certification = {
    name: string;
    image?: string;
    /** Shown inside the placeholder tile when there is no badge to display yet. */
    short?: string;
    inProgress?: boolean;
};

const certifications: Certification[] = [
    { name: "ISC2 Certified in Cybersecurity (CC)", image: isc2CcImage },
    { name: "AWS Certified Cloud Practitioner", image: awsCcpImage },
    { name: "Microsoft Certified: Security, Compliance, and Identity Fundamentals (SC-900)", image: sc900Image },
    { name: "Microsoft Certified: Information Security Administrator (SC-401)", short: "SC-401", inProgress: true },
];


function Chips({ items }: { items: string[] }) {
    return (
        <div className="flex-chips">
            {items.map((label, i) => (
                <Chip key={i} className="chip" label={label} />
            ))}
        </div>
    );
}

export default function Expertise() {
    return (
        <div className="container" id="expertise">
            <div className="skills-container">
                <h1>Expertise</h1>

                <div className="skills-grid">
                    {/* Cloud & DevSecOps */}
                    <div className="skill">
                        <div className="skill-icon">
                            <FontAwesomeIcon icon={faCloud} size="2x" />
                            <FontAwesomeIcon icon={faMagnifyingGlass} size="2x" style={{ marginLeft: ".5rem" }} />
                        </div>
                        <h3>Cloud & DevSecOps</h3>
                        <p>
                            Engineered <strong>end-to-end cloud security automation</strong> in AWS: delivered <strong>hardened deployments</strong>,
                            advanced <strong>WAF configurations</strong>, secure <strong>CI/CD with OIDC</strong>, and strict <strong>least-privilege</strong> controls.
                        </p>
                        <Chips items={cloudDevSecOps} />
                    </div>

                    {/* Security Operations & Compliance */}
                    <div className="skill">
                        <div className="skill-icon">
                            <FontAwesomeIcon icon={faShieldHalved} size="2x" />
                            <FontAwesomeIcon icon={faCheck} size="2x" style={{ marginLeft: ".5rem" }} />
                        </div>
                        <h3>Security Operations & Compliance</h3>
                        <p>
                            Hands-on experience in <strong>threat detection, hunting, and incident response</strong> (writing rules, enriching logs, and investigations).<br />
                            Led <strong>ISO 27001 audits</strong>, ensuring compliance while optimizing processes to <strong>reduce certification costs</strong>.
                        </p>
                        <Chips items={socDetection} />
                    </div>

                    {/* Offensive Security & Malware */}
                    <div className="skill">
                        <div className="skill-icon">
                            <FontAwesomeIcon icon={faBug} size="2x" />
                            <FontAwesomeIcon icon={faSkullCrossbones} size="2x" style={{ marginLeft: ".5rem" }} />
                        </div>
                        <h3>Offensive Security & Malware</h3>
                        <p>
                            From <strong>pentesting</strong> to <strong>reverse engineering</strong>, I apply offensive techniques
                            to strengthen defenses: CTFs, custom tooling, and safe malware labs.
                            My CTF write-ups are available{" "}
                                                        <Link to="/writeups" style={{ color: "var(--principal)" }}>
                                                            here
                                                        </Link>.
                        </p>
                        <Chips items={offensiveMalware} />
                    </div>
                </div>

                {/* Extra: Projects and Certifications */}
                <div className="skills-extra">

                    <div className="extra-col">
                        <h4><FontAwesomeIcon icon={faLock} /> Certifications</h4>
                        <div className="certification-images">
                            {certifications.map((cert) => (
                                <div
                                    className={`certification${cert.inProgress ? " certification--pending" : ""}`}
                                    key={cert.name}
                                >
                                    {cert.image ? (
                                        <img src={cert.image} alt={cert.name} className="certification-image" />
                                    ) : (
                                        <div
                                            className="certification-placeholder"
                                            role="img"
                                            aria-label={`${cert.name} — in progress`}
                                        >
                                            {cert.short}
                                        </div>
                                    )}
                                    <span className="certification-name">
                                        {cert.name}{cert.inProgress ? " — in progress" : ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
